var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import debug from "debug";
import ky from "ky";
import { MessageText, SessionRequestBuilder, SessionResponseClassEnum, } from "./models";
import { codeError } from "./utils";
import { Util } from "@auvious/common";
import { MessageAttachment, MessageCustomerJoin, SessionResponseStructuredMessageBodyText, SessionResponseStructuredMessageBodyEvent, SessionResponseStructuredMessage, MessageCustomerTyping, MessageClearConversation, SessionResponseStructuredMessageBodyBot, MessageStructured, } from "./models/webMessenger";
import { AUVIOUS_VALID_URL_REGEX, WIDGET_COBROWSE_REQUEST_PREFIX, WIDGET_MESSAGE_UUID, WIDGET_REFERRER_ID, WIDGET_MODE, OriginModeEnum, ErrorCode, WIDGET_CONVERSATION_TYPE, KEY_SESSION_TOKEN, KEY_SESSION_CODE, DEFAULT_AGENT_ID, DEFAULT_CUSTOMER_ID, } from "./models/enums";
import { localStore } from "@auvious/utils";
import { Restorable } from "./models/Restorable";
const log = debug("auvious:integrations");
// @deprecated
const KEY_WEB_MESSAGING = "auvious.web.messaging";
export class GenesysWebMessagingService extends Restorable {
    constructor(config, handlers) {
        var _a, _b;
        super();
        this.config = config;
        this.handlers = handlers;
        this.attachmentConstraints = {
            mimeType: ".jpeg,.jpg,.png,.gif",
            fileSize: 1024 * 1024 * 10,
            friendlyFileSize: "10 MiB",
        };
        this.sessionNew = false;
        this.sessionType = "guest";
        // consecutive connection attempts
        this.connAttempts = 0;
        // all unique file names associated with uploading in the current session
        this.fileNames = new Set();
        this.uploads = {};
        // attachment id to its last known url
        this.fileUrls = {};
        this.downloads = {}; // <'file id' to promise>
        this.pingId = Util.uuidgen();
        this.sessionToken = localStore.getItem(KEY_SESSION_TOKEN);
        if (config.customDomain) {
            this.customDomain = new RegExp(
            // escape any special characters in the stringToFind to prevent them from being interpreted as regex operators.
            config.customDomain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
        }
        this.refreshSessionToken();
        (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.ready) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    reconnect(contact, conversationType) {
        return __awaiter(this, void 0, void 0, function* () {
            const code = this.getCodeFromLegacySession() || this.getCodeFromSession();
            if (code) {
                this.createChat(contact, conversationType, code, false);
            }
        });
    }
    /**
     * restore whatever was stored in local storage, if any
     */
    getCodeFromSession() {
        try {
            const previousSession = localStore.getItem(KEY_SESSION_CODE);
            if (previousSession) {
                const code = JSON.parse(previousSession);
                return code;
            }
        }
        catch (ex) {
            log.error("Error reconnecting previous session", ex);
        }
        return undefined;
    }
    /**
     * Backwards compatibility to support the previous way we stored data to local storage
     * @returns an object that is compliant with the newer version
     */
    getCodeFromLegacySession() {
        try {
            const code = {
                applicationId: this.config.applicationId,
            };
            const legacySession = localStore.getItem(KEY_WEB_MESSAGING);
            if (legacySession) {
                const { authenticationCode } = JSON.parse(legacySession);
                if (authenticationCode) {
                    code.token = authenticationCode;
                }
                return code;
            }
        }
        catch (ex) {
            log.error("Error reconnecting previous session", ex);
        }
        return undefined;
    }
    clearSessionToken() {
        localStore.removeItem(KEY_SESSION_TOKEN);
        this.sessionToken = undefined;
    }
    refreshSessionToken() {
        if (!this.sessionToken) {
            this.sessionToken = Util.uuidgen();
            localStore.setItem(KEY_SESSION_TOKEN, this.sessionToken);
        }
    }
    enableHealthCheck() {
        if (this.pinger) {
            clearInterval(this.pinger);
        }
        this.pinger = window.setInterval(() => {
            this.send({
                action: "echo",
                message: {
                    type: "Text",
                    text: this.pingId,
                },
            });
        }, 4 * 60 * 1000);
    }
    openChannel() {
        var _a, _b;
        if (this.session && this.session.readyState !== WebSocket.CLOSED) {
            return;
        }
        const isReconnecting = !!this.session;
        (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.connecting) === null || _b === void 0 ? void 0 : _b.call(_a);
        // open web socket and connect
        const socketURL = new URL("/v1", `wss://webmessaging.${this.config.pcEnvironment}`);
        socketURL.searchParams.append("deploymentId", this.config.pcDeploymentId);
        this.connAttempts++;
        this.session = new WebSocket(socketURL);
        this.session.addEventListener("error", () => {
            var _a, _b;
            if (this.connAttempts === 1) {
                (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, codeError(new Error("WebSocket error. Invalid deploymentId or connection error"), ErrorCode.INIT_CHAT_SERVICE_FAILED));
            }
        });
        this.session.addEventListener("message", (e) => {
            var _a, _b, _c, _d;
            const message = JSON.parse(e.data);
            log("Process message", message);
            switch (message.class) {
                case SessionResponseClassEnum.SessionResponse:
                    this.processSessionResponse(message);
                    break;
                case SessionResponseClassEnum.StructuredMessage:
                    this.processStructuredMessage(message);
                    break;
                case SessionResponseClassEnum.JWTResponse:
                    this.processJWTResponse(message);
                    break;
                case SessionResponseClassEnum.PresignedUrlResponse:
                    this.processPresignedUrlResponse(message);
                    break;
                case SessionResponseClassEnum.UploadSuccessEvent:
                    this.processUploadSuccessEvent(message);
                    break;
                case SessionResponseClassEnum.StringResponse:
                    this.handleStringError(message);
                    break;
                case SessionResponseClassEnum.GenerateUrlError:
                    (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, codeError(new Error(message.body.errorMessage), message.body.errorCode));
                    break;
                case SessionResponseClassEnum.ErrorMessage:
                    (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.error) === null || _d === void 0 ? void 0 : _d.call(_c, codeError(new Error(message.body.errorMessage), message.body.errorCode));
                    break;
                default:
                    log("Message not parsed", message);
                    break;
            }
        });
        return new Promise((resolve, reject) => {
            this.session.addEventListener("open", () => {
                var _a, _b, _c, _d;
                this.connAttempts = 0;
                this.configureSession(this.config.pcDeploymentId, this.sessionToken, false);
                this.enableHealthCheck();
                resolve();
                if (isReconnecting) {
                    (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.reconnected) === null || _b === void 0 ? void 0 : _b.call(_a);
                }
                else {
                    (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.connected) === null || _d === void 0 ? void 0 : _d.call(_c);
                }
            }, { once: true });
            this.session.addEventListener("close", () => {
                var _a, _b;
                reject(new Error("WebSocket closed"));
                if (this.pinger) {
                    clearInterval(this.pinger);
                }
                if (this.session && this.connAttempts < 2) {
                    setTimeout(() => this.openChannel(), 5000);
                }
                else {
                    (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.disconnected) === null || _b === void 0 ? void 0 : _b.call(_a);
                    this.endChat();
                }
            }, { once: true });
        });
    }
    configureSession(deploymentId, token, startNew) {
        if (this.sessionType === "guest") {
            this.send({
                action: "configureSession",
                deploymentId,
                token,
                startNew,
            });
        }
        else {
            this.send({
                action: "configureAuthenticatedSession",
                deploymentId,
                token,
                startNew,
                data: {
                    code: this.authenticatedSessionToken,
                },
            });
        }
    }
    send(message) {
        var _a;
        try {
            (_a = this.session) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify(message));
        }
        catch (ex) {
            log("Error sending web message", ex);
            throw ex;
        }
    }
    processSessionResponse(message) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (message.code === 200) {
            /*
              If the session is read only, a new session must be requested in the configureSession request.
              This will remove the old session and allow messaging to flow again.
            */
            if (message.body.readOnly) {
                this.clearSessionToken();
                this.refreshSessionToken();
                this.configureSession(this.config.pcDeploymentId, this.sessionToken, true);
                return;
            }
            this.sessionConnected = message.body.connected;
            this.joinChat();
            /*
              These sessions remain active until 72 hours after the last message sent by either the agent or the customer.
              If the customer leaves and then configures another session within this time it will be reconnected to the same session.
              If the customer returns after this time a new session is created.
              If we always send a new sessionToken, then a new session will always be created.
            */
            this.sessionNew = message.body.newSession;
            if (!this.sessionNew) {
                // in order to retrieve the message history, we need to request for a 1-min jwt token
                // and then do the request
                this.requestJWT();
            }
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.started({
                id: null,
                uri: null,
                jwt: null,
                memberId: DEFAULT_CUSTOMER_ID,
            });
            // if we enable the comment below, an alert will not happen on agent side because no messages will be sent
            // the customer will have to send a message for a new interaction to reach genesys
            // if (this.sessionNew) {
            (_c = (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.agentNotified) === null || _c === void 0 ? void 0 : _c.call(_b);
            // }
            (_e = (_d = this.handlers) === null || _d === void 0 ? void 0 : _d.agentJoined) === null || _e === void 0 ? void 0 : _e.call(_d, {
                id: DEFAULT_AGENT_ID,
                joinDate: new Date().toISOString(),
                displayName: null,
            });
        }
        else {
            (_g = (_f = this.handlers) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.call(_f, codeError(new Error(message.body), message.code));
        }
    }
    processStructuredMessage(message) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const messageResponse = new SessionResponseStructuredMessage(message);
        if (messageResponse.body instanceof SessionResponseStructuredMessageBodyEvent) {
            let readOnly;
            switch (messageResponse.body.getEventType()) {
                case "Typing":
                    switch (messageResponse.body.direction) {
                        case "Inbound":
                            // notification that the customer is typing
                            break;
                        case "Outbound":
                            // notification that the agent is typing
                            if (!this.agentTypingEnabled) {
                                return;
                            }
                            if (((_a = messageResponse.body.getEvent().typing) === null || _a === void 0 ? void 0 : _a.type) === "On") {
                                (_c = (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.agentTypingStarted) === null || _c === void 0 ? void 0 : _c.call(_b);
                                // normally an agent typing event carries the duration as well. we don't get an 'off' event
                                this.agentTypingTimeout = setTimeout(() => {
                                    var _a, _b;
                                    (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.agentTypingStopped) === null || _b === void 0 ? void 0 : _b.call(_a);
                                    this.agentTypingTimeout = undefined;
                                }, (_e = (_d = messageResponse.body.getEvent().typing) === null || _d === void 0 ? void 0 : _d.duration) !== null && _e !== void 0 ? _e : 5000);
                            }
                            else if (((_f = messageResponse.body.getEvent().typing) === null || _f === void 0 ? void 0 : _f.type) === "Off") {
                                (_h = (_g = this.handlers) === null || _g === void 0 ? void 0 : _g.agentTypingStopped) === null || _h === void 0 ? void 0 : _h.call(_g);
                            }
                            break;
                    }
                    break;
                case "Presence":
                    switch (messageResponse.body.direction) {
                        case "Inbound":
                            // customer joined, maybe started()?
                            break;
                        case "Outbound":
                            switch (messageResponse.body.getPresenceType()) {
                                // currently no Outbound / Join event is documented
                                // case "Join":
                                //   this.handlers?.agentJoined?.({
                                //     id: messageResponse.getMetadata()?.correlationId,
                                //     joinDate: new Date().toISOString(),
                                //     displayName: null,
                                //   });
                                //   break;
                                case "Disconnect":
                                    // if we get a disconnect event, the 'readOnly' property carries if the session has ended or not
                                    readOnly = messageResponse.getMetadata().readOnly;
                                    // alt: (messageResponse.body.getEvent() as EventCustomerDisconnect).readOnly;
                                    this.notifyChatEnded();
                                    if (readOnly) {
                                        this.clearSessionToken();
                                        this.refreshSessionToken();
                                    }
                                    break;
                                case "Clear":
                                    this.notifyChatEnded();
                                    this.clearSessionToken();
                                    this.refreshSessionToken();
                                    break;
                            }
                            break;
                    }
                    break;
            }
        }
        else if (messageResponse.body instanceof
            SessionResponseStructuredMessageBodyText &&
            messageResponse.body.text !== this.pingId) {
            if (messageResponse.body.direction === "Outbound" &&
                this.agentTypingTimeout) {
                (_j = this.handlers) === null || _j === void 0 ? void 0 : _j.agentTypingStopped();
                clearTimeout(this.agentTypingTimeout);
                this.agentTypingTimeout = undefined;
            }
            this.parseTextMessage({
                id: ((_k = messageResponse.body.metadata) === null || _k === void 0 ? void 0 : _k[WIDGET_MESSAGE_UUID]) ||
                    messageResponse.body.id,
                channel: messageResponse.body.channel,
                direction: messageResponse.body.direction,
                type: messageResponse.body.type,
                text: messageResponse.body.text,
                content: messageResponse.body.content,
            });
        }
        else if (messageResponse.body instanceof SessionResponseStructuredMessageBodyBot) {
            this.parseStructuredMessage(messageResponse.body);
        }
    }
    processJWTResponse(message) {
        var _a;
        if (message.code === 200) {
            this.getMessageHistory(message.body.jwt);
        }
        else {
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error(codeError(new Error("jwt-error-response"), message.code));
        }
    }
    processPresignedUrlResponse(message) {
        if (message.body.fileName) {
            this.downloads[message.body.attachmentId].onRenewedUrl(message);
        }
        else {
            const upload = Object.values(this.uploads).find((upload) => upload.state === "requestedUrl");
            if (upload) {
                upload.onPresignedUrl(message);
            }
            else {
                this.uploadNextFile();
            }
        }
    }
    handleStringError(message) {
        var _a, _b, _c;
        switch (message.code) {
            case ErrorCode.WEB_MESSAGING_SESSION_CLOSED:
            case ErrorCode.WEB_MESSAGING_SESSION_EXPIRED:
            case ErrorCode.WEB_MESSAGING_SESSION_NOT_FOUND:
                // trigger reconnect
                (_a = this.session) === null || _a === void 0 ? void 0 : _a.close();
                break;
            default:
                (_c = (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.call(_b, codeError(new Error(message.body), message.code));
        }
    }
    processUploadSuccessEvent(message) {
        const task = Object.entries(this.uploads).find(([_, value]) => value.attachmentId ===
            message.body.attachmentId);
        task === null || task === void 0 ? void 0 : task[1].onUploaded(message);
    }
    uploadNextFile() {
        var _a;
        // it is fine in this context
        const uploads = Object.values(this.uploads);
        if (uploads.find((upload) => upload.state === "requestedUrl")) {
            return;
        }
        (_a = uploads.find((upload) => upload.state === "pending")) === null || _a === void 0 ? void 0 : _a.requestPresignedUrl();
    }
    requestJWT() {
        this.send({
            action: "getJwt",
            token: this.sessionToken,
        });
    }
    getMessageHistory(jwtToken) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield ky
                    .get("api/v2/webmessaging/messages", {
                    prefixUrl: this.prefixUrl,
                    hooks: {
                        beforeRequest: [
                            (request) => {
                                request.headers.set("Authorization", `Bearer ${jwtToken}`);
                                request.headers.set("Origin", window.location.origin);
                            },
                        ],
                    },
                })
                    .json();
                log("got message history", response);
                if (response.entities) {
                    response.entities.forEach((message) => {
                        switch (message.type) {
                            case "Structured":
                                this.parseStructuredMessage(message, true);
                                break;
                            case "Text":
                                this.parseTextMessage(message, true);
                                break;
                        }
                    });
                    // this.parseTextMessages(response.entities, true);
                }
            }
            catch (ex) {
                (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error(codeError(ex, ErrorCode.CONVERSATION_MESSAGE_PARSE_FAILED));
            }
        });
    }
    parseStructuredMessage(message, isHistory = false) {
        var _a, _b, _c, _d, _e;
        const msg = {
            id: message.id,
            type: "bot",
            isLocal: message.direction === "Inbound",
            timestamp: new Date(message.channel.time),
            status: "ack",
            text: message.text,
            senderId: message.direction,
            metadata: message.metadata,
            senderName: (_b = (_a = message.channel) === null || _a === void 0 ? void 0 : _a.from) === null || _b === void 0 ? void 0 : _b.nickname,
        };
        if ((_c = message.content) === null || _c === void 0 ? void 0 : _c.length) {
            message.content.forEach((content) => {
                switch (content.contentType) {
                    case "QuickReply":
                        if (!msg.quickReplies) {
                            msg.quickReplies = [];
                        }
                        msg.quickReplies.push(content.quickReply);
                        break;
                    case "ButtonResponse":
                        if (!msg.buttonResponse) {
                            msg.buttonResponse = [];
                        }
                        msg.buttonResponse.push(content.buttonResponse);
                        break;
                }
            });
        }
        (_e = (_d = this.handlers).messageReceived) === null || _e === void 0 ? void 0 : _e.call(_d, msg);
    }
    parseTextMessage(message, isHistory = false) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        try {
            if (message.channel.from) {
                (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.memberSet({
                    avatarImageUrl: message.channel.from.image,
                    displayName: message.channel.from.nickname,
                    id: DEFAULT_AGENT_ID,
                    joinDate: message.channel.time,
                    role: "AGENT",
                });
            }
            if ((_b = message.content) === null || _b === void 0 ? void 0 : _b.length) {
                message.content.forEach((content) => {
                    var _a, _b, _c, _d;
                    switch (content.contentType) {
                        case "Attachment":
                            this.fileUrls[content.attachment.id] = content.attachment.url;
                            (_b = (_a = this.handlers).messageReceived) === null || _b === void 0 ? void 0 : _b.call(_a, {
                                id: message.id,
                                file: {
                                    id: content.attachment.id,
                                    name: message.direction === "Inbound"
                                        ? message.text // in case of 'inbound', it is being set by us as the unique id we generate
                                        : content.attachment.filename,
                                    size: content.attachment.fileSize,
                                },
                                type: "file",
                                isLocal: message.direction === "Inbound",
                                timestamp: new Date(message.channel.time),
                                status: "ack",
                                senderId: message.direction,
                                senderName: (_d = (_c = message.channel) === null || _c === void 0 ? void 0 : _c.from) === null || _d === void 0 ? void 0 : _d.nickname,
                                metadata: message.metadata,
                            });
                            break;
                    }
                });
            }
            else {
                if (message.type === "Text") {
                    if (AUVIOUS_VALID_URL_REGEX.test(message.text) ||
                        ((_c = this.customDomain) === null || _c === void 0 ? void 0 : _c.test(message.text))) {
                        // do not open a past call
                        if (!isHistory) {
                            log("webMessaging: room created", message.text);
                            (_e = (_d = this.handlers).roomCreated) === null || _e === void 0 ? void 0 : _e.call(_d, message.text);
                        }
                    }
                    else if (((_f = message.text) === null || _f === void 0 ? void 0 : _f.indexOf(WIDGET_COBROWSE_REQUEST_PREFIX)) > -1) {
                        // do not open a past co-browse request
                        if (!isHistory) {
                            log("CXOne: cobrowse requested", message.text);
                            // auvious:cobrowse-request:<request-type>:<ticket>
                            const request = message.text.split(":");
                            const requestType = request[2];
                            const ticket = request[3];
                            (_h = (_g = this.handlers).cobrowseRequested) === null || _h === void 0 ? void 0 : _h.call(_g, ticket, requestType);
                        }
                    }
                    else {
                        (_k = (_j = this.handlers).messageReceived) === null || _k === void 0 ? void 0 : _k.call(_j, {
                            id: message.id,
                            text: message.text,
                            type: "text",
                            isLocal: message.direction === "Inbound",
                            timestamp: new Date(message.channel.time),
                            status: "ack",
                            senderId: message.direction,
                            senderName: (_m = (_l = message.channel) === null || _l === void 0 ? void 0 : _l.from) === null || _m === void 0 ? void 0 : _m.nickname,
                            metadata: message.metadata || ((_p = (_o = message.channel) === null || _o === void 0 ? void 0 : _o.metadata) === null || _p === void 0 ? void 0 : _p.customAttributes),
                        });
                    }
                }
            }
        }
        catch (ex) {
            log("could not parse web-messaging message. Reason:", ex);
        }
    }
    get prefixUrl() {
        // return `${API_SOURCE_MAP[this.config.pcEnvironment]}`;
        return `https://api.${this.config.pcEnvironment}`;
    }
    joinChat() {
        if (this.autoStartEnabled) {
            this.send(new SessionRequestBuilder(this.sessionToken)
                .setMessage(new MessageCustomerJoin())
                .setMetadata(this.getCustomerMetadata())
                .build());
        }
    }
    useAuthenticatedSessionCode(token) {
        this.sessionType = token ? "authenticated" : "guest";
        this.authenticatedSessionToken = token;
    }
    /** interface implementations ***/
    createChat(contact, conversationType, code, saveCode = true) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (code === null || code === void 0 ? void 0 : code.token) {
                this.useAuthenticatedSessionCode(code.token);
            }
            // save into current session if it exists
            localStore.setItem(KEY_SESSION_CODE, JSON.stringify(Object.assign(Object.assign({}, code), { token: saveCode ? code.token : undefined })));
            // start guest session
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.starting) === null || _b === void 0 ? void 0 : _b.call(_a);
            this.connAttempts = 0;
            yield this.openChannel();
            // join
            this.sessionContact = contact;
            this.sessionConversationType = conversationType;
        });
    }
    endChat() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session) {
                return;
            }
            if (this.clearConversationEnabled) {
                this.send(new SessionRequestBuilder(this.sessionToken)
                    .setMessage(new MessageClearConversation())
                    .build());
                this.clearSessionToken();
                this.refreshSessionToken();
            }
            this.notifyChatEnded();
        });
    }
    notifyChatEnded() {
        var _a, _b, _c;
        if (this.session) {
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.customerLeft();
            (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.agentLeft();
            (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.ended();
        }
        this.destroy();
    }
    destroy() {
        const ws = this.session;
        this.session = null;
        ws === null || ws === void 0 ? void 0 : ws.close();
        localStore.removeItem(KEY_SESSION_CODE);
    }
    sendTyping() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.customerTypingEnabled) {
                return;
            }
            const request = new SessionRequestBuilder(this.sessionToken)
                .setMessage(new MessageCustomerTyping("On"))
                .build();
            this.send(request);
            return null;
        });
    }
    getCustomerMetadata() {
        return Object.assign(Object.assign({ 
            // https://developer.genesys.cloud/forum/t/add-name-to-alerting-tile/19788/3
            name: [this.sessionContact.firstName, this.sessionContact.lastName]
                .join(" ")
                .trim() }, this.sessionContact), { 
            // "context." prefix is inherited specifically for the webchat integration.
            // on the UI all the customer attributes, when they reach the agent are prefixed by genesys with 'context'
            // we do it here as well to keep compatibility with webChat when parsing customer attributes.
            [`context.${WIDGET_CONVERSATION_TYPE}`]: this.sessionConversationType, [`context.${WIDGET_REFERRER_ID}`]: window.origin, [`context.${WIDGET_MODE}`]: this.config.kioskMode
                ? OriginModeEnum.kiosk
                : OriginModeEnum.website });
    }
    acknowledgeMessages() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    sendMessage(message, bodyType, metadata) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let meta = Object.assign({ [WIDGET_MESSAGE_UUID]: Util.uuidgen() }, metadata);
            let content;
            let msg;
            // we have no other way to pass customer attributes, so we use the 'notice' message, which are
            // the two messages that reach the agent and are automatically sent by 'agentNotified' on the widget side
            switch (bodyType) {
                case "notice":
                    meta = Object.assign(Object.assign({}, meta), this.getCustomerMetadata());
                    msg = new MessageText(message);
                    break;
                case "standard":
                    msg = new MessageText(message);
                    break;
                case "content":
                    content =
                        meta.content;
                    delete meta.content;
                    msg = new MessageStructured(message);
                    break;
            }
            // message typing: invalid message format
            // else {
            //   this.send(
            //     new SessionRequestBuilder(this.sessionToken)
            //       .setMessage(new MessageCustomerTyping("Off"))
            //       .build()
            //   );
            // }
            const request = new SessionRequestBuilder(this.sessionToken)
                .setMessage(msg)
                .setMetadata(meta)
                .setContent(content);
            this.send(request.build());
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.messageReceived({
                id: meta[WIDGET_MESSAGE_UUID],
                type: "text",
                isLocal: true,
                text: message,
                timestamp: new Date(),
                status: "sent",
                metadata: meta,
                senderId: "Inbound", // this.sessionToken, // customer sender id
            });
        });
    }
    /**
     * Flow
     * 1. send message via webSocket to get a presigned url
     * 2. listen for the message and get the presigned url
     * 3. upload the attachment to the presigned url
     * 4. wait for upload success message that will contain the attachment id
     * 5. send a websocket text message containing the attachment id
     * 6. listen for a webscoket text message with a content=attachment to handle and bubble to ui via the handlers
     * @param inputFileRef HTML input[type=file]
     * @returns promise that resolves at step 5
     */
    sendFile(inputFileRef) {
        return new Promise((resolve, reject) => {
            var _a, _b;
            // 0. prepare data
            const file = inputFileRef.files.item(0);
            if (!file) {
                return reject(new Error("no-file-found"));
            }
            // find an unique file name for this session
            let fileName = file.name;
            for (let i = 1; true; i++) {
                if (this.fileNames.has(fileName)) {
                    fileName = `(${i}) ${file.name}`;
                }
                else {
                    break;
                }
            }
            this.fileNames.add(fileName);
            log(`uploading file ${fileName}`);
            const tempAttachmentId = Util.uuidgen();
            const msg = {
                id: tempAttachmentId,
                file: {
                    id: tempAttachmentId,
                    name: fileName,
                    size: file.size,
                },
                type: "file",
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: "Inbound",
            };
            (_b = (_a = this.handlers).messageSent) === null || _b === void 0 ? void 0 : _b.call(_a, msg);
            const setState = (state) => {
                this.uploads[fileName].state = state;
                log(`uploading file ${fileName} state: ${state}`);
                switch (state) {
                    case "uploading":
                        this.uploadNextFile();
                        break;
                    case "failed":
                    case "done":
                        cleanup();
                        this.uploadNextFile();
                }
            };
            // 1. Request a presigned url
            const requestPresignedUrl = () => {
                var _a, _b, _c;
                try {
                    this.send({
                        action: "onAttachment",
                        fileName,
                        fileType: file.type,
                        fileSize: file.size,
                        token: this.sessionToken,
                    });
                    setState("requestedUrl");
                }
                catch (_d) {
                    setState("failed");
                    (_b = (_a = this.handlers).messageError) === null || _b === void 0 ? void 0 : _b.call(_a, Object.assign(Object.assign({}, msg), { status: "error" }));
                    // TODO: fix error type
                    reject(codeError(new Error("upload request for presigned-url failed"), ErrorCode.GENESYS_API_ERROR));
                    (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.error(codeError(new Error("upload request for presigned-url failed"), ErrorCode.GENESYS_API_ERROR));
                }
            };
            // 2. Listen for the message and get the presigned url
            const onPresignedUrl = (message) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d, _e;
                if (message.code === 200) {
                    this.uploads[fileName].attachmentId = message.body.attachmentId;
                    yield uploadFile(message);
                }
                else {
                    setState("failed");
                    (_d = (_c = this.handlers).messageError) === null || _d === void 0 ? void 0 : _d.call(_c, Object.assign(Object.assign({}, msg), { status: "error" }));
                    reject(codeError(new Error(message.body), message.code));
                    // TODO: Genesys API gives more information than that
                    (_e = this.handlers) === null || _e === void 0 ? void 0 : _e.error(codeError(new Error("upload request for presigned-url failed"), message.code));
                }
            });
            // 3. upload the attachment to the presigned url
            const uploadFile = (message) => __awaiter(this, void 0, void 0, function* () {
                var _f, _g, _h;
                try {
                    setState("uploading");
                    yield ky.put(message.body.url, {
                        body: file,
                        headers: {
                            "x-amz-tagging": message.body.headers["x-amz-tagging"],
                        },
                    });
                    log(`${fileName} uploaded successfully, waiting for "UploadSuccessEvent"`);
                }
                catch (ex) {
                    setState("failed");
                    (_g = (_f = this.handlers).messageError) === null || _g === void 0 ? void 0 : _g.call(_f, Object.assign(Object.assign({}, msg), { status: "error" }));
                    (_h = this.handlers) === null || _h === void 0 ? void 0 : _h.error(codeError(ex, ErrorCode.CONVERSATION_MESSAGE_PARSE_FAILED));
                    reject(codeError(ex, ErrorCode.WEB_MESSAGING_ATTACHMENT_UPLOAD_FAILED));
                }
            });
            // 4. wait for upload response message to send attachment message
            const onUploaded = (message) => {
                var _a, _b, _c;
                if (message.code === 200) {
                    setState("done");
                    this.send(new SessionRequestBuilder(this.sessionToken)
                        .setMessage(new MessageAttachment(fileName))
                        .setContent([
                        {
                            contentType: "Attachment",
                            attachment: {
                                id: message.body
                                    .attachmentId,
                            },
                        },
                    ])
                        .build());
                    resolve();
                }
                else {
                    setState("failed");
                    (_b = (_a = this.handlers).messageError) === null || _b === void 0 ? void 0 : _b.call(_a, Object.assign(Object.assign({}, msg), { status: "error" }));
                    reject(codeError(new Error(message.body), message.code));
                    (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.error(codeError(new Error("file-upload-failure"), message.code));
                }
            };
            const cleanup = () => {
                delete this.uploads[fileName];
            };
            // TODO:? implement cancel based on state
            const cancel = () => { };
            this.uploads[fileName] = {
                state: "pending",
                requestPresignedUrl,
                onPresignedUrl,
                onUploaded,
                cancel,
            };
            this.uploadNextFile();
        });
    }
    downloadFile(file) {
        // ongoing download request
        if (file.id in this.downloads) {
            return;
        }
        return new Promise((resolve, reject) => {
            const setState = (state) => {
                this.downloads[file.id].state = state;
                switch (state) {
                    case "failed":
                    case "done":
                        cleanup();
                }
            };
            // 1. Try download the file with the url we already have
            const fetchFile = () => __awaiter(this, void 0, void 0, function* () {
                try {
                    setState("fetchUrl");
                    const blob = yield ky.get(this.fileUrls[file.id]).blob();
                    showLink(blob);
                }
                catch (ex) {
                    renewUrl();
                }
            });
            // 2. Link expired, renew
            const renewUrl = () => __awaiter(this, void 0, void 0, function* () {
                var _a;
                try {
                    setState("renewUrl");
                    // download url is valid only for 10 mins.
                    this.send({
                        action: "getAttachment",
                        token: this.sessionToken,
                        attachmentId: file.id,
                    });
                }
                catch (ex) {
                    setState("failed");
                    // TODO: fix error type
                    reject(codeError(new Error("download url renewal failed"), ErrorCode.GENESYS_API_ERROR));
                    (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.error(codeError(new Error("download url renewal failed"), ErrorCode.GENESYS_API_ERROR));
                }
            });
            // 3. Download file with renewed url
            const onRenewedUrl = (message) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                if (message.code === 200) {
                    setState("fetchRenewedUrl");
                    this.fileUrls[file.id] = message.body.url;
                    const blob = yield ky.get(this.fileUrls[file.id]).blob();
                    showLink(blob);
                }
                else {
                    setState("failed");
                    reject(codeError(new Error(message.body), message.code));
                    (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.error(codeError(new Error("download request for presigned-url failed"), message.code));
                }
            });
            // 4. Download file using anchor
            const showLink = (blob) => {
                setState("show");
                const link = document.createElement("a");
                link.setAttribute("download", file.name);
                link.setAttribute("target", "_blank");
                link.href = URL.createObjectURL(blob);
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(link.href);
                resolve();
                setState("done");
            };
            const cleanup = () => {
                delete this.downloads[file.id];
            };
            // TODO:? implement cancel based on state
            const cancel = () => { };
            this.downloads[file.id] = {
                state: "fetchUrl",
                onRenewedUrl,
                cancel,
            };
            fetchFile();
        });
    }
    customerConnected() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // todo: check if the customer is connected to an existing session (somehow)
            // return Promise.resolve(this.sessionConnected && !!this.sessionToken);
            return ((_a = this.session) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN;
        });
    }
    get autoStartEnabled() {
        var _a, _b, _c, _d, _e, _f;
        return ((_f = (_e = (_d = (_c = (_b = (_a = this.config.pcGenesysWebMessagingConfiguration) === null || _a === void 0 ? void 0 : _a.messenger) === null || _b === void 0 ? void 0 : _b.apps) === null || _c === void 0 ? void 0 : _c.conversations) === null || _d === void 0 ? void 0 : _d.autoStart) === null || _e === void 0 ? void 0 : _e.enabled) !== null && _f !== void 0 ? _f : false);
    }
    get clearConversationEnabled() {
        var _a, _b, _c, _d, _e, _f;
        return ((_f = (_e = (_d = (_c = (_b = (_a = this.config.pcGenesysWebMessagingConfiguration) === null || _a === void 0 ? void 0 : _a.messenger) === null || _b === void 0 ? void 0 : _b.apps) === null || _c === void 0 ? void 0 : _c.conversations) === null || _d === void 0 ? void 0 : _d.conversationClear) === null || _e === void 0 ? void 0 : _e.enabled) !== null && _f !== void 0 ? _f : false);
    }
    get agentTypingEnabled() {
        var _a, _b, _c, _d, _e;
        return ((_e = (_d = (_c = (_b = (_a = this.config.pcGenesysWebMessagingConfiguration) === null || _a === void 0 ? void 0 : _a.messenger) === null || _b === void 0 ? void 0 : _b.apps) === null || _c === void 0 ? void 0 : _c.conversations) === null || _d === void 0 ? void 0 : _d.showAgentTypingIndicator) !== null && _e !== void 0 ? _e : false);
    }
    get customerTypingEnabled() {
        var _a, _b, _c, _d, _e;
        return ((_e = (_d = (_c = (_b = (_a = this.config.pcGenesysWebMessagingConfiguration) === null || _a === void 0 ? void 0 : _a.messenger) === null || _b === void 0 ? void 0 : _b.apps) === null || _c === void 0 ? void 0 : _c.conversations) === null || _d === void 0 ? void 0 : _d.showUserTypingIndicator) !== null && _e !== void 0 ? _e : false);
    }
}
//# sourceMappingURL=genesys.web.messaging.service.js.map